import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { indianCities } from '../data/indianCities';
import './WaitlistForm.css';

const WaitlistForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile: '',
    email: '',
    business_name: '',
    city: '',
    is_gst_registered: '',
    business_type: '',
    daily_customer_count: '',
    is_using_accounting_tool: '',
    accounting_tool_name: '',
    pref_accounting_lang: '',
    pref_accounting_lang_other: '',
    accounting_challange: '',
    accounting_challange_other: '',
    accounting_budget: '',
    reference_source: '',
    early_access: false,
    features: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // City search state
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [filteredCities, setFilteredCities] = useState(indianCities);

  const businessTypes = [
    'Retail Shop',
    'Wholesale/Distribution',
    'Restaurant/Food Service',
    'Manufacturing',
    'Trading',
    'Services',
    'Other'
  ];

  const languages = [
    'Hindi', 'English', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 
    'Gujarati', 'Kannada', 'Malayalam', 'Odia', 'Punjabi', 'Other'
  ];

  const accountingTools = [
    'Tally',
    'Zoho Books',
    'Khatabook',
    'Vyapaar',
    'Dukaan',
    'Other'
  ];

  const challenges = [
    'Takes too much time',
    'Too many errors',
    'I forget to write the transactions properly.',
    'Don\'t understand accounting',
    'Accountant is expensive',
    'Can\'t track cash properly',
    'GST filing is complicated',
    'Other'
  ];

  const referenceSources = [
    'Google Search',
    'Facebook/Instagram',
    'WhatsApp',
    'Friend/Referral',
    'LinkedIn',
    'YouTube',
    'Other'
  ];

  // Handle city search
  useEffect(() => {
    if (citySearchTerm) {
      const filtered = indianCities.filter(city =>
        city.toLowerCase().includes(citySearchTerm.toLowerCase())
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities(indianCities);
    }
  }, [citySearchTerm]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCitySearch = (e) => {
    const value = e.target.value;
    setCitySearchTerm(value);
    setFormData(prev => ({ ...prev, city: value }));
    setShowCityDropdown(true);
    
    if (errors.city) {
      setErrors(prev => ({ ...prev, city: '' }));
    }
  };

  const selectCity = (city) => {
    setFormData(prev => ({ ...prev, city }));
    setCitySearchTerm(city);
    setShowCityDropdown(false);
  };

  const handleCityBlur = () => {
    // Delay hiding dropdown to allow click events to fire
    setTimeout(() => {
      setShowCityDropdown(false);
    }, 200);
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Full name is required';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.business_name.trim()) {
      newErrors.business_name = 'Business name is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.is_gst_registered) {
      newErrors.is_gst_registered = 'Please select an option';
    }

    if (!formData.business_type) {
      newErrors.business_type = 'Business type is required';
    }

    if (!formData.daily_customer_count) {
      newErrors.daily_customer_count = 'Please select an option';
    }

    if (!formData.is_using_accounting_tool) {
      newErrors.is_using_accounting_tool = 'Please select an option';
    }

    // Conditional validation for accounting_tool_name
    if ((formData.is_using_accounting_tool === '1' || formData.is_using_accounting_tool === '2') 
        && !formData.accounting_tool_name) {
      newErrors.accounting_tool_name = 'Please specify which tool you use';
    }

    if (!formData.pref_accounting_lang) {
      newErrors.pref_accounting_lang = 'Preferred language is required';
    }

    // Validate 'Other' for language
    if (formData.pref_accounting_lang === 'Other' && !formData.pref_accounting_lang_other.trim()) {
      newErrors.pref_accounting_lang_other = 'Please specify the language';
    }

    if (!formData.accounting_challange) {
      newErrors.accounting_challange = 'Please select your biggest challenge';
    }

    // Validate 'Other' for challenge
    if (formData.accounting_challange === 'Other' && !formData.accounting_challange_other.trim()) {
      newErrors.accounting_challange_other = 'Please describe your challenge';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus({ type: 'error', message: 'Please fix the errors in the form' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare data for submission
      const submitData = {
        customer_name: formData.customer_name,
        mobile: formData.mobile,
        email: formData.email,
        business_name: formData.business_name,
        city: formData.city,
        is_gst_registered: formData.is_gst_registered,
        business_type: formData.business_type,
        daily_customer_count: formData.daily_customer_count,
        is_using_accounting_tool: formData.is_using_accounting_tool,
        accounting_tool_name: (formData.is_using_accounting_tool === '1' || 
                              formData.is_using_accounting_tool === '2') 
                              ? formData.accounting_tool_name : null,
        pref_accounting_lang: formData.pref_accounting_lang === 'Other' 
                              ? formData.pref_accounting_lang_other 
                              : formData.pref_accounting_lang,
        accounting_challange: formData.accounting_challange === 'Other' 
                              ? formData.accounting_challange_other 
                              : formData.accounting_challange,
        accounting_budget: formData.accounting_budget || null,
        reference_source: formData.reference_source || null,
        early_access: formData.early_access ? 1 : 0,
        features: formData.features || null
      };

      const { error } = await supabase
        .from('talkbooks_waitlist')
        .insert([submitData]);

      if (error) throw error;

      setSubmitStatus({ 
        type: 'success', 
        message: 'Thank you for joining our waitlist! We\'ll be in touch soon.' 
      });
      
      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        onClose();
        setFormData({
          customer_name: '',
          mobile: '',
          email: '',
          business_name: '',
          city: '',
          is_gst_registered: '',
          business_type: '',
          daily_customer_count: '',
          is_using_accounting_tool: '',
          accounting_tool_name: '',
          pref_accounting_lang: '',
          pref_accounting_lang_other: '',
          accounting_challange: '',
          accounting_challange_other: '',
          accounting_budget: '',
          reference_source: '',
          early_access: false,
          features: ''
        });
        setCitySearchTerm('');
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to submit form. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if accounting tool question should be shown
  const showAccountingToolQuestion = formData.is_using_accounting_tool === '1' || 
                                     formData.is_using_accounting_tool === '2';

  const showLanguageOther = formData.pref_accounting_lang === 'Other';
  const showChallengeOther = formData.accounting_challange === 'Other';

  if (!isOpen) return null;

  return (
    <div className="waitlist-modal-overlay" onClick={onClose}>
      <div className="waitlist-form-container" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-button" onClick={onClose} aria-label="Close">
          ✕
        </button>
        
        <div className="waitlist-form-header">
          <h2>Join the Talkbooks Waitlist</h2>
          <p>Help us build the perfect bookkeeping solution for your business</p>
        </div>

        <form onSubmit={handleSubmit} className="waitlist-form">
          {/* Essential Information Section */}
          <div className="form-section">
            <h3>Essential Information</h3>
            
            <div className="form-group">
              <label htmlFor="customer_name">
                Full Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="customer_name"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                className={errors.customer_name ? 'error' : ''}
              />
              {errors.customer_name && <span className="error-message">{errors.customer_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="mobile">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                maxLength="10"
                className={errors.mobile ? 'error' : ''}
              />
              {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="business_name">
                Business Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                className={errors.business_name ? 'error' : ''}
              />
              {errors.business_name && <span className="error-message">{errors.business_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="city">
                City <span className="required">*</span>
              </label>
              <div className="city-search-container">
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={citySearchTerm}
                  onChange={handleCitySearch}
                  onFocus={() => setShowCityDropdown(true)}
                  onBlur={handleCityBlur}
                  placeholder="Search or type your city name"
                  className={errors.city ? 'error' : ''}
                  autoComplete="off"
                />
                {showCityDropdown && (
                  <div className="city-dropdown">
                    {filteredCities.length > 0 ? (
                      <>
                        {filteredCities.slice(0, 100).map((city, index) => (
                          <div
                            key={index}
                            className="city-option"
                            onClick={() => selectCity(city)}
                          >
                            {city}
                          </div>
                        ))}
                        {filteredCities.length > 100 && (
                          <div className="city-option disabled">
                            ... and {filteredCities.length - 100} more. Keep typing to narrow down.
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="city-option disabled">
                        No cities found. Your entry will be saved as: "{citySearchTerm}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>

            <div className="form-group">
              <label>
                Is your Business registered under GST? <span className="required">*</span>
              </label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="is_gst_registered"
                    value="Yes"
                    checked={formData.is_gst_registered === 'Yes'}
                    onChange={handleChange}
                  />
                  Yes
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="is_gst_registered"
                    value="No"
                    checked={formData.is_gst_registered === 'No'}
                    onChange={handleChange}
                  />
                  No
                </label>
              </div>
              {errors.is_gst_registered && <span className="error-message">{errors.is_gst_registered}</span>}
            </div>
          </div>

          {/* Qualification Questions Section */}
          <div className="form-section">
            <h3>Business Details</h3>

            <div className="form-group">
              <label htmlFor="business_type">
                Type of Business <span className="required">*</span>
              </label>
              <select
                id="business_type"
                name="business_type"
                value={formData.business_type}
                onChange={handleChange}
                className={errors.business_type ? 'error' : ''}
              >
                <option value="">Select business type</option>
                {businessTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.business_type && <span className="error-message">{errors.business_type}</span>}
            </div>

            <div className="form-group">
              <label>
                How many Customers do you serve daily? <span className="required">*</span>
              </label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="daily_customer_count"
                    value="1"
                    checked={formData.daily_customer_count === '1'}
                    onChange={handleChange}
                  />
                  Less than 50
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="daily_customer_count"
                    value="2"
                    checked={formData.daily_customer_count === '2'}
                    onChange={handleChange}
                  />
                  50-100
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="daily_customer_count"
                    value="3"
                    checked={formData.daily_customer_count === '3'}
                    onChange={handleChange}
                  />
                  100-500
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="daily_customer_count"
                    value="4"
                    checked={formData.daily_customer_count === '4'}
                    onChange={handleChange}
                  />
                  500-1,000
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="daily_customer_count"
                    value="5"
                    checked={formData.daily_customer_count === '5'}
                    onChange={handleChange}
                  />
                  1,000-5,000
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="daily_customer_count"
                    value="6"
                    checked={formData.daily_customer_count === '6'}
                    onChange={handleChange}
                  />
                  More than 5,000
                </label>
              </div>
              {errors.daily_customer_count && <span className="error-message">{errors.daily_customer_count}</span>}
            </div>

            <div className="form-group">
              <label>
                Do you currently use any accounting/bookkeeping software? <span className="required">*</span>
              </label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="is_using_accounting_tool"
                    value="1"
                    checked={formData.is_using_accounting_tool === '1'}
                    onChange={handleChange}
                  />
                  Yes, I use
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="is_using_accounting_tool"
                    value="2"
                    checked={formData.is_using_accounting_tool === '2'}
                    onChange={handleChange}
                  />
                  No, but my accountant uses it
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="is_using_accounting_tool"
                    value="3"
                    checked={formData.is_using_accounting_tool === '3'}
                    onChange={handleChange}
                  />
                  No, I use paper books
                </label>
              </div>
              {errors.is_using_accounting_tool && <span className="error-message">{errors.is_using_accounting_tool}</span>}
            </div>

            {/* Conditional Question */}
            {showAccountingToolQuestion && (
              <div className="form-group conditional-question">
                <label htmlFor="accounting_tool_name">
                  Which accounting/bookkeeping software do you/your accountant use? <span className="required">*</span>
                </label>
                <select
                  id="accounting_tool_name"
                  name="accounting_tool_name"
                  value={formData.accounting_tool_name}
                  onChange={handleChange}
                  className={errors.accounting_tool_name ? 'error' : ''}
                >
                  <option value="">Select tool</option>
                  {accountingTools.map(tool => (
                    <option key={tool} value={tool}>{tool}</option>
                  ))}
                </select>
                {errors.accounting_tool_name && <span className="error-message">{errors.accounting_tool_name}</span>}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="pref_accounting_lang">
                Preferred Language for Bookkeeping <span className="required">*</span>
              </label>
              <select
                id="pref_accounting_lang"
                name="pref_accounting_lang"
                value={formData.pref_accounting_lang}
                onChange={handleChange}
                className={errors.pref_accounting_lang ? 'error' : ''}
              >
                <option value="">Select language</option>
                {languages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
              {errors.pref_accounting_lang && <span className="error-message">{errors.pref_accounting_lang}</span>}
            </div>

            {/* Conditional "Other" input for language */}
            {showLanguageOther && (
              <div className="form-group conditional-question">
                <label htmlFor="pref_accounting_lang_other">
                  Please specify your preferred language <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="pref_accounting_lang_other"
                  name="pref_accounting_lang_other"
                  value={formData.pref_accounting_lang_other}
                  onChange={handleChange}
                  maxLength="50"
                  placeholder="Enter language name (max 50 characters)"
                  className={errors.pref_accounting_lang_other ? 'error' : ''}
                />
                <small className="char-count">{formData.pref_accounting_lang_other.length}/50 characters</small>
                {errors.pref_accounting_lang_other && <span className="error-message">{errors.pref_accounting_lang_other}</span>}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="accounting_challange">
                Biggest Bookkeeping Challenge <span className="required">*</span>
              </label>
              <select
                id="accounting_challange"
                name="accounting_challange"
                value={formData.accounting_challange}
                onChange={handleChange}
                className={errors.accounting_challange ? 'error' : ''}
              >
                <option value="">Select challenge</option>
                {challenges.map(challenge => (
                  <option key={challenge} value={challenge}>{challenge}</option>
                ))}
              </select>
              {errors.accounting_challange && <span className="error-message">{errors.accounting_challange}</span>}
            </div>

            {/* Conditional "Other" input for challenge */}
            {showChallengeOther && (
              <div className="form-group conditional-question">
                <label htmlFor="accounting_challange_other">
                  Please describe your challenge <span className="required">*</span>
                </label>
                <textarea
                  id="accounting_challange_other"
                  name="accounting_challange_other"
                  value={formData.accounting_challange_other}
                  onChange={handleChange}
                  maxLength="200"
                  rows="3"
                  placeholder="Describe your bookkeeping challenge (max 200 characters)"
                  className={errors.accounting_challange_other ? 'error' : ''}
                />
                <small className="char-count">{formData.accounting_challange_other.length}/200 characters</small>
                {errors.accounting_challange_other && <span className="error-message">{errors.accounting_challange_other}</span>}
              </div>
            )}
          </div>

          {/* Optional Questions Section */}
          <div className="form-section">
            <h3>Optional Information</h3>

            <div className="form-group">
              <label>How much do you spend monthly on bookkeeping/accounting?</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="accounting_budget"
                    value="1"
                    checked={formData.accounting_budget === '1'}
                    onChange={handleChange}
                  />
                  ₹0 (I do it myself)
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="accounting_budget"
                    value="2"
                    checked={formData.accounting_budget === '2'}
                    onChange={handleChange}
                  />
                  ₹1,000-5,000
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="accounting_budget"
                    value="3"
                    checked={formData.accounting_budget === '3'}
                    onChange={handleChange}
                  />
                  ₹5,000-10,000
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="accounting_budget"
                    value="4"
                    checked={formData.accounting_budget === '4'}
                    onChange={handleChange}
                  />
                  ₹10,000-20,000
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="accounting_budget"
                    value="5"
                    checked={formData.accounting_budget === '5'}
                    onChange={handleChange}
                  />
                  More than ₹20,000
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="reference_source">How did you hear about us?</label>
              <select
                id="reference_source"
                name="reference_source"
                value={formData.reference_source}
                onChange={handleChange}
              >
                <option value="">Select source</option>
                {referenceSources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="early_access"
                  checked={formData.early_access}
                  onChange={handleChange}
                />
                Yes, I want to test the beta version (We'll contact you in February 2026)
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="features">Any specific features you need?</label>
              <textarea
                id="features"
                name="features"
                value={formData.features}
                onChange={handleChange}
                rows="4"
                placeholder="Tell us about features you'd like to see..."
              />
            </div>
          </div>

          {submitStatus && (
            <div className={`submit-status ${submitStatus.type}`}>
              {submitStatus.message}
            </div>
          )}

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WaitlistForm;
